/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { useIntl } from 'react-intl';

import SkuAttributeModelBuilder from '@cimpress-technology/sku-attribute-model-builder';
import TextField from '@cimpress/react-components/lib/TextField';

import auth from '../../auth';
import PackCalculatorDisplay from './PackCalculatorDisplay';
import MappingProps from './mappingProps';
import messages from './messages';

const packBodyStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 20px;
`;

type PackMappingViewerProps = {
  mapping: MappingProps;
  mappingsLoaded: Set<String>;
  setMappingsLoaded: (mappingsLoaded: Set<String>) => void;
};

const PackMappingViewer = ({ mapping, mappingsLoaded, setMappingsLoaded }: PackMappingViewerProps) => {
  const isProduction = process.env.REACT_APP_ENVIRONMENT_NAME === 'Production';
  const { formatMessage } = useIntl();

  // If packCalculatorList exists and has items, use it; otherwise fall back to packCalculator
  const hasPackCalculatorList = Boolean(
    mapping._embedded.packCalculatorList && mapping._embedded.packCalculatorList.length > 0,
  );

  const packCalculatorIds = hasPackCalculatorList
    ? mapping._embedded.packCalculatorList!.map(pc => pc.id)
    : [mapping._embedded.packCalculator.id];

  // Product Info content (used in both layouts)
  const productInfoContent = (
    <>
      <h4>Product Info</h4>
      {mapping.skus && (
        <div>
          <h6>{formatMessage(messages.skus)}</h6>
          <TextField value={mapping.skus.join(', ')} readOnly onChange={val => val} />
        </div>
      )}
      {mapping._links?.attributeModel?.href && (
        <>
          <h6>{formatMessage(messages.attributeModel)}</h6>
          <SkuAttributeModelBuilder
            token={auth.getAccessToken()}
            resourceUrl={mapping._links.attributeModel.href}
            isReadOnly
            isProduction={isProduction}
          />
        </>
      )}
    </>
  );

  return (
    <div>
      <hr />

      {/* OLD LAYOUT: Single calculator (backward compatible layout) */}
      {!hasPackCalculatorList ? (
        <PackCalculatorDisplay
          key={packCalculatorIds[0]}
          packCalculatorId={packCalculatorIds[0]}
          mappingsLoaded={mappingsLoaded}
          setMappingsLoaded={setMappingsLoaded}
          showCalculatorNameAsSubheading={false}
          showPackFormulasHeading={true}
          useLegacySingleCalculatorLayout={true}
          productInfoContent={productInfoContent}
        />
      ) : (
        /* NEW LAYOUT: Multiple calculators  */
        <>
          {/* Display pack group name if it exists, otherwise show default heading */}
          <h2 style={{ paddingBottom: 10 }}>{mapping.packGroupName || formatMessage(messages.packGroup)}</h2>

          <div css={packBodyStyle}>
            <div>
              {/* Render each pack calculator */}
              {packCalculatorIds.map((packCalculatorId, index) => (
                <PackCalculatorDisplay
                  key={packCalculatorId}
                  packCalculatorId={packCalculatorId}
                  mappingsLoaded={mappingsLoaded}
                  setMappingsLoaded={setMappingsLoaded}
                  showCalculatorNameAsSubheading={true}
                  showPackFormulasHeading={index === 0}
                  useLegacySingleCalculatorLayout={false}
                />
              ))}
            </div>
            <div>{productInfoContent}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default PackMappingViewer;
